import React from "react";
import axios from "axios";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import {
  X,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle2,
  Lightbulb,
  Loader2,
  Share2,
  Copy,
  Check,
  Clock,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { API } from "@/lib/api";
import { fmtInt, fmtMoney, fmtPct, fmtCompact } from "@/lib/format";
import { SemaforoDot, SemaforoBadge } from "@/components/Semaforo";

const RED = "#E2333F";
const AMBER = "#F59E0B";

const DIR = {
  alza: { icon: TrendingUp, color: "text-emerald-600", label: "Alza" },
  baja: { icon: TrendingDown, color: "text-red-600", label: "Baja" },
  estable: { icon: Minus, color: "text-slate-500", label: "Estable" },
};
const PRIO = {
  alta: "bg-red-100 text-red-700 ring-red-200",
  media: "bg-amber-100 text-amber-700 ring-amber-200",
  baja: "bg-slate-100 text-slate-600 ring-slate-200",
};

function DirBadge({ dir }) {
  const d = DIR[dir] || DIR.estable;
  const Icon = d.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold ${d.color}`}>
      <Icon className="w-3.5 h-3.5" /> {d.label}
    </span>
  );
}

function ProjectionChart({ context, analysis }) {
  const hist = context?.metrics?.ventas?.trend || [];
  const proj = analysis?.proyeccion?.meses || [];
  const combined = hist.map((p) => ({ mes: p.mes, real: p.valor, proj: null }));
  if (combined.length && hist.length) combined[combined.length - 1].proj = hist[hist.length - 1].valor;
  proj.forEach((m) => combined.push({ mes: m.mes, real: null, proj: m.ventas_estimadas }));
  return (
    <div style={{ height: 240 }} data-testid="ai-projection-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={combined} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E9ECF1" vertical={false} />
          <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={48} tickFormatter={fmtCompact} />
          <Tooltip formatter={(v) => (v == null ? "-" : `${fmtInt(v)} kg`)} contentStyle={{ borderRadius: 4, border: "1px solid #E2E8F0", fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line name="Histórico" type="monotone" dataKey="real" stroke={RED} strokeWidth={2.5} dot={{ r: 2 }} connectNulls />
          <Line name="Proyección IA" type="monotone" dataKey="proj" stroke={AMBER} strokeWidth={2.5} strokeDasharray="6 4" dot={{ r: 3, fill: AMBER }} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function Section({ title, icon: Icon, children, testId }) {
  return (
    <div className="mb-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-4 h-4 text-[#E2333F]" />}
        <h3 className="font-display font-bold text-slate-800 text-sm uppercase tracking-wide">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export function AIPanel({ context, analysis }) {
  return (
    <div className="p-6 lg:p-8" data-testid="ai-analysis-panel">
      <div className="bg-gradient-to-br from-[#E2333F] to-[#9c1f29] text-white rounded-sm p-5 mb-6">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold opacity-90 mb-1">
          <Sparkles className="w-4 h-4" /> Resumen ejecutivo · IA
        </div>
        <p className="text-sm leading-relaxed">{analysis.resumen_ejecutivo}</p>
      </div>

      <Section title="Proyección a 3 meses" icon={TrendingUp} testId="ai-proyeccion">
        <div className="flex items-center gap-3 mb-2">
          <DirBadge dir={analysis.proyeccion?.direccion} />
          <span className="text-sm text-slate-500">{analysis.proyeccion?.texto}</span>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
          <ProjectionChart context={context} analysis={analysis} />
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3">
          {(analysis.proyeccion?.meses || []).map((m) => (
            <div key={m.mes} className="bg-amber-50 border border-amber-200 rounded-sm p-2">
              <div className="text-[10px] uppercase tracking-wide text-amber-700 font-bold">{m.mes}</div>
              <div className="font-display font-black text-slate-900 text-sm">{fmtInt(m.ventas_estimadas)} <span className="text-xs text-slate-400">kg</span></div>
              <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">{m.comentario}</div>
            </div>
          ))}
        </div>
      </Section>

      {analysis.tendencias?.length > 0 && (
        <Section title="Tendencias clave" testId="ai-tendencias">
          <div className="space-y-2">
            {analysis.tendencias.map((t, i) => (
              <div key={i} className="flex items-start gap-3 bg-white border border-slate-200 rounded-sm p-3">
                <div className="shrink-0 mt-0.5"><DirBadge dir={t.direccion} /></div>
                <div>
                  <div className="font-semibold text-sm text-slate-800">{t.metrica}</div>
                  <div className="text-xs text-slate-500">{t.comentario}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Diagnóstico" testId="ai-diagnostico">
        <p className="text-sm text-slate-600 leading-relaxed">{analysis.diagnostico}</p>
      </Section>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {analysis.fortalezas?.length > 0 && (
          <Section title="Fortalezas" icon={CheckCircle2}>
            <ul className="space-y-1.5">
              {analysis.fortalezas.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
          </Section>
        )}
        {analysis.riesgos?.length > 0 && (
          <Section title="Riesgos" icon={AlertTriangle}>
            <ul className="space-y-1.5">
              {analysis.riesgos.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                  <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" /> {r}
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>

      {analysis.recomendaciones?.length > 0 && (
        <Section title="Recomendaciones" icon={Lightbulb} testId="ai-recomendaciones">
          <div className="space-y-2.5">
            {analysis.recomendaciones.map((r, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-sm p-3">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-display font-bold text-sm text-slate-900">{r.titulo}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-sm ring-1 ${PRIO[r.prioridad] || PRIO.media}`}>
                    {r.prioridad}
                  </span>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">{r.detalle}</p>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

export function DataPanel({ context }) {
  const t = context.totals;
  const sem = t.semaforo || { status: t.status };
  const trend = context?.metrics?.ventas?.trend || [];
  return (
    <div className="p-6 lg:p-8 bg-slate-50 h-full" data-testid="ai-data-panel">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="font-display font-bold text-slate-800 text-sm uppercase tracking-wide">Información del nodo</h3>
        <SemaforoBadge status={sem.status} />
      </div>

      <div className="grid grid-cols-2 gap-2 mb-5">
        {[
          { l: "Tendencia ventas", v: `${fmtInt(t.tendencia_kilos)} kg` },
          { l: "Acumulado", v: `${fmtInt(t.acumulado)} kg` },
          { l: "Mes anterior", v: `${fmtInt(t.mes_anterior)} kg` },
          { l: "Año anterior", v: `${fmtInt(t.ano_anterior)} kg` },
          { l: "Utilidad gestión", v: fmtMoney(t.utilidad_por_kilo) },
          { l: "HG Neto", v: fmtPct(t.hg_neto_pct) },
          { l: "Unidades taller", v: fmtInt(t.unidades_taller) },
          { l: "Momentum", v: `${fmtInt((sem.momentum || 1) * 100)}%` },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-sm px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{s.l}</div>
            <div className="font-display font-bold text-slate-900 text-sm">{s.v}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-5">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Ventas Jul–Jun (kilos)</div>
        <div className="grid grid-cols-3 gap-1.5">
          {trend.map((d, i) => {
            const last = i === trend.length - 1;
            return (
              <div key={d.mes} className={`rounded-sm px-2 py-1.5 border ${last ? "bg-[#E2333F]/8 border-[#E2333F]/30" : "bg-slate-50 border-slate-100"}`}>
                <div className="text-[10px] uppercase text-slate-400 font-semibold leading-none">{d.mes}</div>
                <div className={`font-display font-bold text-xs tabular-nums mt-0.5 ${last ? "text-[#E2333F]" : "text-slate-900"}`}>{fmtCompact(d.valor)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {context.children?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2">
            Desglose ({context.children.length})
          </div>
          <div className="space-y-1 max-h-[320px] overflow-y-auto">
            {[...context.children].sort((a, b) => b.tendencia_kilos - a.tendencia_kilos).map((c, i) => (
              <div key={i} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-sm hover:bg-slate-50">
                <div className="flex items-center gap-2 min-w-0">
                  <SemaforoDot status={c.status} size={8} />
                  <span className="text-sm text-slate-700 truncate">{c.name}</span>
                </div>
                <span className="font-display font-bold text-xs text-slate-900 tabular-nums shrink-0">{fmtInt(c.tendencia_kilos)} kg</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function cleanError(detail) {
  if (!detail) return "Intenta de nuevo en unos segundos.";
  const d = String(detail);
  if (d.includes("<") || /cloudflare|html|origin web server/i.test(d) || d.length > 220) {
    return "El servicio de análisis no respondió a tiempo. Intenta de nuevo en unos segundos.";
  }
  return d;
}

function useAnalysis(open, node) {
  const [state, setState] = React.useState({ status: "idle" });
  React.useEffect(() => {
    if (!open || !node) {
      setState({ status: "idle" });
      return;
    }
    let cancelled = false;
    let timer;
    setState({ status: "loading" });
    const poll = async (jobId) => {
      try {
        const { data } = await axios.get(`${API}/ai/job/${jobId}`);
        if (cancelled) return;
        if (data.status === "done") setState({ status: "done", context: data.context, analysis: data.analysis });
        else if (data.status === "error") setState({ status: "error", detail: data.detail });
        else timer = setTimeout(() => poll(jobId), 2500);
      } catch (e) {
        if (!cancelled) setState({ status: "error", detail: e?.response?.data?.detail || "Error de conexión" });
      }
    };
    const start = async () => {
      try {
        const { data } = await axios.post(`${API}/ai/analysis`, { level: node.level, id: node.id });
        if (cancelled) return;
        if (data.status === "done") setState({ status: "done", context: data.context, analysis: data.analysis });
        else timer = setTimeout(() => poll(data.job_id), 1500);
      } catch (e) {
        if (!cancelled) setState({ status: "error", detail: e?.response?.data?.detail || "No se pudo iniciar el análisis" });
      }
    };
    start();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, node?.level, node?.id]);
  return state;
}

function ShareButton({ node, context, analysis }) {
  const [open, setOpen] = React.useState(false);
  const [share, setShare] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const url = share ? `${window.location.origin}/share/${share.token}` : "";

  const generate = async () => {
    setOpen(true);
    if (share || loading) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/share`, {
        level: node.level,
        id: node.id,
        context,
        analysis,
      });
      setShare(data);
    } catch (e) {
      setShare({ error: e?.response?.data?.detail || "No se pudo generar el enlace" });
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    const done = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    };
    const fallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (e) {
        /* ignore */
      }
    };
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(url).then(done).catch(() => {
          fallback();
          done();
        });
      } else {
        fallback();
        done();
      }
    } catch (e) {
      fallback();
      done();
    }
  };

  const expiresTxt = share?.expires_at
    ? new Date(share.expires_at).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "";

  return (
    <div className="relative">
      <button
        onClick={generate}
        data-testid="ai-share-btn"
        className="flex items-center gap-1.5 h-9 px-3 rounded-sm bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-colors"
      >
        <Share2 className="w-4 h-4" />
        <span className="hidden sm:inline">Compartir</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[80]" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 top-11 z-[90] w-[300px] bg-white text-slate-800 rounded-md shadow-2xl border border-slate-200 p-4"
            data-testid="ai-share-popover"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="font-display font-bold text-sm text-slate-900">Compartir análisis</span>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700">
                <X className="w-4 h-4" />
              </button>
            </div>
            {loading ? (
              <div className="flex flex-col items-center gap-2 py-6 text-slate-500">
                <Loader2 className="w-6 h-6 animate-spin text-[#E2333F]" />
                <span className="text-xs">Generando enlace…</span>
              </div>
            ) : share?.error ? (
              <div className="text-sm text-red-600 py-4">{share.error}</div>
            ) : share ? (
              <div className="flex flex-col items-center">
                <div className="p-2 bg-white rounded-sm ring-1 ring-slate-200" data-testid="ai-share-qr">
                  <QRCodeSVG value={url} size={170} level="M" fgColor="#0F172A" />
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-amber-600 font-semibold mt-3">
                  <Clock className="w-3.5 h-3.5" /> Vigencia 24 h · expira {expiresTxt}
                </div>
                <div className="w-full flex items-center gap-1 mt-3">
                  <input
                    readOnly
                    value={url}
                    className="flex-1 min-w-0 text-[11px] border border-slate-200 rounded-sm px-2 py-1.5 bg-slate-50 text-slate-600"
                    data-testid="ai-share-url"
                  />
                  <button
                    onClick={copy}
                    data-testid="ai-share-copy"
                    className="h-7 w-8 flex items-center justify-center rounded-sm bg-[#E2333F] text-white hover:bg-[#c92a36]"
                  >
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
                <p className="text-[10px] text-slate-400 mt-2 text-center">
                  Escanea el QR o comparte el enlace. Vista de solo lectura.
                </p>
              </div>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

export function AIAnalysis({ open, onOpenChange, node }) {
  const state = useAnalysis(open, node);

  React.useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onOpenChange(false);
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  if (!open) return null;

  const levelLabel = { grupo: "Grupo", division: "División", zona: "Zona", regional: "Regional", empresa: "Empresa" };
  const ctx = state.status === "done" ? state.context : null;

  return (
    <div className="fixed inset-0 z-[70] bg-white flex flex-col zae-fade" data-testid="ai-fullscreen">
      <div className="h-16 shrink-0 bg-[#0F172A] text-white flex items-center justify-between px-6 border-b border-black/20">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-sm bg-[#E2333F] flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-white/60 font-bold leading-none">Análisis IA · Claude</div>
            <div className="font-display font-black text-lg leading-tight">
              {ctx ? `${levelLabel[ctx.level]} · ${ctx.name}` : (node?.id ? "Cargando…" : levelLabel[node?.level])}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {state.status === "done" && <ShareButton node={node} context={state.context} analysis={state.analysis} />}
          <button
            onClick={() => onOpenChange(false)}
            data-testid="ai-close"
            className="w-9 h-9 flex items-center justify-center rounded-sm hover:bg-white/15 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        {state.status === "loading" || state.status === "idle" ? (
          <div className="h-full flex flex-col items-center justify-center text-center gap-3 text-slate-500" data-testid="ai-loading">
            <Loader2 className="w-10 h-10 text-[#E2333F] animate-spin" />
            <div className="font-display font-bold text-lg text-slate-700">La IA está analizando los datos…</div>
            <div className="text-sm">Generando proyección de tendencias y recomendaciones</div>
            <div className="text-xs text-slate-400">Puede tomar hasta ~1 minuto en nodos grandes</div>
          </div>
        ) : state.status === "error" ? (
          <div className="h-full flex flex-col items-center justify-center text-center gap-2 text-slate-500 px-6">
            <AlertTriangle className="w-8 h-8 text-red-500" />
            <div className="font-semibold">No se pudo generar el análisis</div>
            <div className="text-sm max-w-md">{cleanError(state.detail)}</div>
            <button
              onClick={() => onOpenChange(false)}
              className="mt-3 h-9 px-4 rounded-sm bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#1e293b]"
            >
              Cerrar e intentar de nuevo
            </button>
          </div>
        ) : state.status === "done" ? (
          <div className="h-full grid grid-cols-1 lg:grid-cols-[1fr_420px] overflow-hidden">
            <div className="overflow-y-auto border-r border-slate-200">
              <AIPanel context={state.context} analysis={state.analysis} />
            </div>
            <div className="overflow-y-auto hidden lg:block">
              <DataPanel context={state.context} />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
